(() => {
class DDContinuousCarousel extends HTMLElement {
  constructor() {
    super();
    this.pauseReasons = new Set();
    this.explicitlyPaused = false;
    this.lastFrame = null;
    this.raf = null;
    this.pointerId = null;
    this.pointerGesture = null;
    this.visible = true;
    this.originalItems = [];
    this.loopClones = [];
    this.loopOrigin = 0;
    this.loopSpan = 0;
    this.loopSignature = '';
    this.normalizingLoop = false;
    this.motionPosition = null;
    this.touchScrollTimer = null;
    this.viewportFrame = null;
  }

  connectedCallback() {
    this.track = this.querySelector('[data-dd-continuous-track]');
    this.motionButton = this.querySelector('[data-dd-carousel-motion]');
    this.previousButton = this.querySelector('button[name="previous"]');
    this.nextButton = this.querySelector('button[name="next"]');
    this.progress = this.querySelector('[data-dd-carousel-progress]');
    this.indicator = this.querySelector('[data-dd-carousel-indicator]');
    if (!this.track) {
      window.setTimeout(() => { if (this.isConnected) this.connectedCallback(); }, 0);
      return;
    }
    if (this.dataset.initialized === 'true') return;
    this.dataset.initialized = 'true';
    this.originalItems = this.items;
    this.abortController = new AbortController();
    const { signal } = this.abortController;

    this.track.addEventListener('scroll', () => {
      if (this.raf === null || this.pauseReasons.has('drag')) this.motionPosition = this.track.scrollLeft;
      if (this.pauseReasons.has('touch-scroll')) this.scheduleTouchScrollResume();
      this.normalizeLoopPosition();
      this.updateControls();
    }, { passive: true, signal });
    this.track.addEventListener('click', (event) => this.activateLoopClone(event), { capture: true, signal });
    this.addEventListener('pointerover', (event) => {
      const control = event.pointerType === 'mouse' ? event.target.closest?.('[data-dd-carousel-hover-pause]') : null;
      if (control && this.contains(control) && !control.contains(event.relatedTarget)) this.pause('media-hover');
      else this.syncArtworkHover(event);
    }, { signal });
    this.addEventListener('pointermove', (event) => this.syncArtworkHover(event), { passive: true, signal });
    this.addEventListener('pointerout', (event) => {
      const control = event.pointerType === 'mouse' ? event.target.closest?.('[data-dd-carousel-hover-pause]') : null;
      if (control && this.contains(control) && !control.contains(event.relatedTarget)) this.resume('media-hover');
      const artwork = event.pointerType === 'mouse' ? event.target.closest?.('.dd-brand-artwork') : null;
      if (artwork && !artwork.contains(event.relatedTarget)) this.resume('media-hover');
    }, { signal });
    this.addEventListener('pointerleave', (event) => { if (event.pointerType === 'mouse') this.resume('media-hover'); }, { signal });
    this.addEventListener('focusin', (event) => {
      this.ensureLoop(true);
      const pointerControl = this.pointerGesture?.target?.closest?.('a, button, input, select, textarea, [role="button"]');
      if (pointerControl?.contains(event.target)) this.resume('focus');
      else this.pause('focus');
    }, { signal });
    this.addEventListener('focusout', () => requestAnimationFrame(() => {
      if (!this.contains(document.activeElement)) this.resume('focus');
    }), { signal });
    this.track.addEventListener('pointerdown', (event) => {
      this.ensureLoop(true);
      clearTimeout(this.touchScrollTimer);
      this.touchScrollTimer = null;
      this.pauseReasons.delete('touch-scroll');
      this.clearDrag();
      this.pointerId = event.pointerId;
      this.pointerGesture = {
        pointerId: event.pointerId,
        pointerType: event.pointerType,
        target: event.target,
        startX: event.clientX,
        startY: event.clientY,
        moved: false,
      };
      const startsOnNativeUgcVideo = this.dataset.ddUgcNativeVideo === 'true' && event.target.closest?.('[data-dd-native-controls="true"] video');
      const startsOnControl = startsOnNativeUgcVideo || event.target.closest?.('a, button, input, select, textarea, [role="button"]');
      if (event.pointerType !== 'touch' && !startsOnControl) {
        try { this.track.setPointerCapture?.(event.pointerId); } catch {}
      }
      this.pause('drag');
    }, { signal });
    this.track.addEventListener('pointermove', (event) => {
      const gesture = this.pointerGesture;
      if (!gesture || event.pointerId !== gesture.pointerId) return;
      if (Math.hypot(event.clientX - gesture.startX, event.clientY - gesture.startY) > 10) gesture.moved = true;
    }, { passive: true, signal });
    const releasePointer = (event) => {
      if (event.pointerId !== this.pointerId) return;
      const gesture = this.pointerGesture;
      if (event.type === 'pointercancel' && gesture?.pointerType === 'touch') this.pause('touch-scroll');
      this.clearDrag();
      if (event.type === 'pointercancel' && gesture?.pointerType === 'touch') this.scheduleTouchScrollResume();
      if (
        event.type === 'pointerup'
        && gesture?.pointerType === 'touch'
        && !gesture.moved
        && !(this.dataset.ddUgcNativeVideo === 'true' && gesture.target?.closest?.('[data-dd-native-controls="true"] video'))
        && !gesture.target?.closest?.('a, button, input, select, textarea, [role="button"]')
        && !gesture.target?.closest?.('[data-dd-ugc-card]')
      ) this.toggleMotion();
    };
    this.track.addEventListener('pointerup', releasePointer, { signal });
    this.track.addEventListener('pointercancel', releasePointer, { signal });
    this.track.addEventListener('lostpointercapture', releasePointer, { signal });
    window.addEventListener('pointerup', releasePointer, { signal });
    window.addEventListener('pointercancel', releasePointer, { signal });
    window.addEventListener('blur', () => this.clearDrag(), { signal });
    this.previousButton?.addEventListener('click', () => this.move(-1), { signal });
    this.nextButton?.addEventListener('click', () => this.move(1), { signal });
    this.motionButton?.addEventListener('click', () => this.toggleMotion(), { signal });

    this.addEventListener('play', (event) => {
      if (!event.target.closest?.('dd-ugc-carousel')) this.setPersistentPause(true);
    }, { capture: true, signal });
    this.addEventListener('dd:media-play', () => this.setPersistentPause(true), { signal });
    this.addEventListener('dd:purchase-open', () => this.pause('purchase'), { signal });
    this.addEventListener('dd:purchase-close', () => this.resume('purchase'), { signal });
    this.mutationObserver = new MutationObserver(() => this.syncPurchasePause());
    this.mutationObserver.observe(this, { subtree: true, childList: true, attributes: true, attributeFilter: ['open', 'aria-hidden'] });

    this.motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.motionQuery.addEventListener?.('change', () => this.syncMotion(), { signal });
    this.resizeObserver = new ResizeObserver(() => this.refresh());
    this.resizeObserver.observe(this);
    if ('IntersectionObserver' in window) {
      this.intersectionObserver = new IntersectionObserver(([entry]) => {
        this.visible = entry?.isIntersecting ?? false;
        if (this.visible) this.ensureLoop();
        this.syncMotion();
      }, { threshold: 0.05 });
      this.intersectionObserver.observe(this);
      this.loopObserver = new IntersectionObserver(([entry]) => {
        if (entry?.isIntersecting) this.refresh();
      }, { rootMargin: '400px' });
      this.loopObserver.observe(this);
    } else {
      // Keep the original cards usable and prime loops on approach in older browsers.
      const refreshViewport = () => {
        if (this.viewportFrame !== null) return;
        this.viewportFrame = requestAnimationFrame(() => {
          this.viewportFrame = null;
          if (this.isConnected) this.refresh();
        });
      };
      window.addEventListener('scroll', refreshViewport, { passive: true, capture: true, signal });
      window.addEventListener('resize', refreshViewport, { passive: true, signal });
    }
    document.addEventListener('visibilitychange', () => this.syncMotion(), { signal });

    this.refresh();
  }

  disconnectedCallback() {
    this.clearDrag();
    this.abortController?.abort();
    this.resizeObserver?.disconnect();
    this.intersectionObserver?.disconnect();
    this.loopObserver?.disconnect();
    if (this.viewportFrame !== null) cancelAnimationFrame(this.viewportFrame);
    this.viewportFrame = null;
    this.mutationObserver?.disconnect();
    this.stopAnimation();
    clearTimeout(this.touchScrollTimer);
    this.touchScrollTimer = null;
    this.pauseReasons.delete('touch-scroll');
    this.removeLoopClones();
    this.motionPosition = null;
    delete this.dataset.initialized;
  }

  get items() { return Array.from(this.track.querySelectorAll(':scope > [data-dd-continuous-item]:not([data-dd-loop-clone])')); }
  get desktop() { return window.matchMedia('(min-width: 990px)').matches; }
  get isCarousel() { return this.dataset.resolvedLayout === 'carousel'; }
  get autoscroll() { return this.dataset.autoscroll === 'true'; }
  get infinite() { return this.dataset.infinite === 'true'; }
  get reducedMotion() { return this.motionQuery?.matches ?? false; }
  get maxScroll() { return Math.max(0, this.track.scrollWidth - this.track.clientWidth); }
  get speed() {
    const value = Number(this.dataset.scrollSpeed || 30);
    return Math.max(10, Math.min(80, Number.isFinite(value) ? value : 30));
  }

  syncArtworkHover(event) {
    if (event.pointerType !== 'mouse' || event.target.closest?.('[data-dd-carousel-hover-pause]')) return;
    const artwork = event.target.closest?.('.dd-brand-artwork');
    const item = artwork?.closest?.('[data-dd-continuous-item], [data-dd-carousel-item]');
    if (!artwork || !item || !this.track.contains(item)) return this.resume('media-hover');
    const box = artwork.getBoundingClientRect();
    const halfWidth = Math.min(48, box.width / 3) / 2;
    const halfHeight = Math.min(48, box.height / 3) / 2;
    const insideCentre = Math.abs(event.clientX - (box.left + box.width / 2)) <= halfWidth
      && Math.abs(event.clientY - (box.top + box.height / 2)) <= halfHeight;
    if (insideCentre) this.pause('media-hover');
    else this.resume('media-hover');
  }

  resolveLayout() {
    if (this.hasAttribute('data-logo-fit-track')) {
      const items = this.items;
      const gap = this.desktop ? 16 : 20;
      const required = items.reduce((sum, item) => {
        const style = getComputedStyle(item);
        const width = parseFloat(item.hasAttribute('data-dd-logo-sized')
          ? style.width
          : style.getPropertyValue('--dd-logo-motion-width'));
        return sum + (width || (this.desktop ? 184 : 138.4));
      }, 0) + Math.max(0, items.length - 1) * gap;
      const fits = required <= this.clientWidth + 1;
      this.toggleAttribute('data-logos-fit', fits);
      if (fits) return 'grid';
      if (this.desktop || Number(this.dataset.mobileColumns || 0) === 0) return 'carousel';
    }
    if (!this.desktop) return Number(this.dataset.mobileColumns || 1) === 0 ? 'carousel' : 'grid';
    const itemCount = this.items.length;
    const columns = Number(this.dataset.desktopColumns || 4);
    const rows = Number(this.dataset.desktopRows || 1);
    const capacityExceeded = itemCount > columns * rows;
    if (capacityExceeded || this.dataset.desktopLayout === 'carousel') return 'carousel';
    if (this.dataset.desktopLayout === 'grid') return 'grid';
    if (this.dataset.autoTrigger === 'from_4') return itemCount >= 4 ? 'carousel' : 'grid';
    if (this.dataset.autoTrigger === 'from_5') return itemCount >= 5 ? 'carousel' : 'grid';
    const gap = parseFloat(getComputedStyle(this.track).columnGap || 0);
    const available = this.track.clientWidth;
    const configuredMinimumCardWidth = Number(this.dataset.autoMinimumCardWidth);
    const minimumCardWidth = Number.isFinite(configuredMinimumCardWidth) && configuredMinimumCardWidth > 0
      ? configuredMinimumCardWidth
      : 220;
    return columns * minimumCardWidth + Math.max(0, columns - 1) * gap > available ? 'carousel' : 'grid';
  }

  refresh() {
    const layout = this.resolveLayout();
    this.dataset.resolvedLayout = layout;
    const mobileColumns = Math.max(1, Number(this.dataset.mobileColumns || 1));
    this.track.style.setProperty('--dd-card-row-mobile-columns', String(mobileColumns));
    this.visible = this.inViewport();
    if (layout === 'grid') {
      this.restoreOriginalOrder();
      this.track.scrollLeft = 0;
    } else if (this.infinite && this.items.length > 1) {
      this.ensureLoop();
    } else {
      this.removeLoopClones();
    }
    this.updateControls();
    this.syncMotion();
  }

  inViewport(margin = 0) {
    const box = this.getBoundingClientRect();
    return box.width > 0 && box.height > 0
      && box.bottom > -margin && box.top < window.innerHeight + margin
      && box.right > -margin && box.left < window.innerWidth + margin;
  }

  ensureLoop(intent = false) {
    if (!this.isCarousel || !this.infinite || this.items.length < 2) return;
    // Native lazy images in detached deep clones can load far below the viewport.
    // Retain the server-rendered originals; create the extra copies on approach.
    if (this.loopClones.length || intent || this.inViewport(400)) this.prepareLoop();
  }

  itemStep() {
    const first = this.items[0];
    if (!first) return 0;
    return first.getBoundingClientRect().width + parseFloat(getComputedStyle(this.track).columnGap || 0);
  }

  move(direction) {
    if (!this.isCarousel) return;
    this.ensureLoop(true);
    this.track.scrollBy({ left: this.itemStep() * direction, behavior: this.reducedMotion ? 'auto' : 'smooth' });
  }

  makeLoopClone(item, index) {
    const clone = item.cloneNode(true);
    const nativeUgcVideo = this.dataset.ddUgcNativeVideo === 'true';
    clone.removeAttribute('id');
    clone.removeAttribute('data-dd-continuous-item');
    clone.removeAttribute('data-dd-carousel-item');
    clone.setAttribute('data-dd-loop-clone', '');
    clone.setAttribute('data-dd-loop-clone-index', String(index));
    if (!nativeUgcVideo) clone.setAttribute('aria-hidden', 'true');
    clone.querySelectorAll('[id]').forEach((node) => node.removeAttribute('id'));
    clone.querySelectorAll('a, button, input, select, textarea, video, audio, iframe, [tabindex]').forEach((node) => {
      if (nativeUgcVideo && node.matches('video') && node.closest('[data-dd-native-controls="true"]')) {
        node.removeAttribute('autoplay');
        node.controls = false;
        node.setAttribute('tabindex', '0');
        const poster = node.closest('[data-dd-ugc-card]')?.querySelector('[data-dd-ugc-native-poster]');
        if (poster) poster.hidden = false;
        return;
      }
      node.setAttribute('tabindex', '-1');
      if (node.matches('input, select, textarea')) node.removeAttribute('name');
      if (node.matches('video, audio')) {
        node.removeAttribute('autoplay');
        node.removeAttribute('src');
        node.preload = 'none';
        node.querySelectorAll('source').forEach((source) => source.removeAttribute('src'));
      }
      if (node.matches('iframe')) {
        node.removeAttribute('src');
        node.removeAttribute('data-src');
      }
    });
    return clone;
  }

  originalControlForCloneTarget(target) {
    const clone = target.closest('[data-dd-loop-clone]');
    if (!clone || !this.track.contains(clone)) return null;
    const original = this.originalItems[Number(clone.dataset.ddLoopCloneIndex)];
    if (!original) return null;
    const selector = 'a, button, input, select, textarea, [role="button"], [tabindex]:not(video):not(audio):not(iframe)';
    const cloneControl = target.closest(selector);
    if (!cloneControl || !clone.contains(cloneControl)) return null;
    const controlIndex = Array.from(clone.querySelectorAll(selector)).indexOf(cloneControl);
    return original.querySelectorAll(selector)[controlIndex] || null;
  }

  activateLoopClone(event) {
    const originalControl = this.originalControlForCloneTarget(event.target);
    if (!originalControl) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    originalControl.click();
  }

  prepareLoop() {
    const items = this.items;
    const gap = parseFloat(getComputedStyle(this.track).columnGap || 0);
    const width = items[0]?.getBoundingClientRect().width || 0;
    if (!this.track.clientWidth || !width) return;
    const signature = `${items.length}:${Math.round(this.track.clientWidth * 10)}:${Math.round(width * 10)}:${Math.round(gap * 10)}`;
    if (this.loopClones.length && signature === this.loopSignature) {
      this.measureLoop();
      this.normalizeLoopPosition();
      return;
    }

    const firstLoop = this.loopClones.length === 0;
    const initialScroll = firstLoop ? this.track.scrollLeft : 0;
    this.removeLoopClones();
    const prefix = items.map((item, index) => this.makeLoopClone(item, index));
    const suffix = items.map((item, index) => this.makeLoopClone(item, index));
    const before = document.createDocumentFragment();
    const after = document.createDocumentFragment();
    prefix.forEach((item) => before.append(item));
    suffix.forEach((item) => after.append(item));
    this.track.prepend(before);
    this.track.append(after);
    this.loopClones = [...prefix, ...suffix];
    this.loopSignature = signature;
    this.measureLoop();
    this.setLoopScroll(this.loopOrigin + initialScroll);
    const ugc = this.closest('dd-ugc-carousel');
    if (firstLoop && ugc?.nativeOwnerItem && ugc.nativeOwnerScroll !== null) {
      ugc.nativeOwnerScroll += this.track.scrollLeft - initialScroll;
    }
  }

  measureLoop() {
    const firstOriginal = this.originalItems[0];
    const firstSuffix = this.loopClones[this.originalItems.length];
    if (!firstOriginal || !firstSuffix) return;
    const peek = Math.min(32, this.itemStep() / 4);
    this.loopOrigin = firstOriginal.offsetLeft - peek;
    this.loopSpan = firstSuffix.offsetLeft - firstOriginal.offsetLeft;
  }

  setLoopScroll(value) {
    if (!Number.isFinite(value)) return;
    this.normalizingLoop = true;
    const previousBehavior = this.track.style.scrollBehavior;
    this.track.style.scrollBehavior = 'auto';
    this.track.scrollLeft = value;
    this.motionPosition = this.track.scrollLeft;
    this.track.style.scrollBehavior = previousBehavior;
    this.normalizingLoop = false;
  }

  normalizeLoopPosition() {
    if (this.normalizingLoop || !this.infinite || this.loopSpan <= 0 || !this.loopClones.length) return;
    const ugc = this.closest('dd-ugc-carousel');
    if (ugc?.activationItem || ugc?.nativeOwnerItem) return;
    let next = this.track.scrollLeft;
    while (next >= this.loopOrigin + this.loopSpan) next -= this.loopSpan;
    while (next < this.loopOrigin) next += this.loopSpan;
    if (Math.abs(next - this.track.scrollLeft) > 0.5) this.setLoopScroll(next);
  }

  removeLoopClones() {
    this.loopClones.forEach((item) => item.remove());
    this.loopClones = [];
    this.loopOrigin = 0;
    this.loopSpan = 0;
    this.loopSignature = '';
  }

  restoreOriginalOrder() {
    this.removeLoopClones();
    this.originalItems.forEach((item) => this.track.append(item));
  }

  animate = (time) => {
    if (!this.shouldAnimate()) return this.stopAnimation();
    if (this.lastFrame !== null) {
      if (!Number.isFinite(this.motionPosition)) this.motionPosition = this.track.scrollLeft;
      this.motionPosition += this.speed * Math.min(64, time - this.lastFrame) / 1000;
      this.track.scrollLeft = this.motionPosition;
      if (this.infinite) this.normalizeLoopPosition();
      else if (this.track.scrollLeft >= this.maxScroll - 0.5) return this.stopAnimation();
      this.updateControls();
    }
    this.lastFrame = time;
    this.raf = requestAnimationFrame(this.animate);
  };

  shouldAnimate() {
    return this.isConnected && this.isCarousel && this.autoscroll && !this.reducedMotion && !this.explicitlyPaused && this.pauseReasons.size === 0 && this.visible && !document.hidden && this.items.length > 1 && (this.infinite ? this.loopClones.length > 0 : this.maxScroll > 1);
  }

  startAnimation() {
    if (this.raf !== null || !this.shouldAnimate()) return;
    this.motionPosition = this.track.scrollLeft;
    this.lastFrame = null;
    this.raf = requestAnimationFrame(this.animate);
  }

  stopAnimation() {
    if (this.raf !== null) cancelAnimationFrame(this.raf);
    this.raf = null;
    this.lastFrame = null;
  }

  syncMotion() {
    if (this.shouldAnimate()) this.startAnimation();
    else this.stopAnimation();
    if (this.motionButton) {
      this.motionButton.hidden = !this.isCarousel || !this.autoscroll || this.reducedMotion;
      this.motionButton.setAttribute('aria-pressed', String(this.explicitlyPaused));
      this.motionButton.setAttribute('aria-label', this.explicitlyPaused ? 'Resume automatic scrolling' : 'Pause automatic scrolling');
      const icon = this.motionButton.querySelector('[data-dd-carousel-motion-icon]');
      const iconText = this.explicitlyPaused ? '▶' : 'Ⅱ';
      if (icon && icon.textContent !== iconText) icon.textContent = iconText;
    }
  }

  toggleMotion() { this.ensureLoop(true); this.setPersistentPause(!this.explicitlyPaused); }
  clearDrag() {
    if (this.pointerId === null && !this.pauseReasons.has('drag')) return;
    const pointerId = this.pointerId;
    this.pointerId = null;
    this.pointerGesture = null;
    this.motionPosition = this.track?.scrollLeft ?? null;
    try {
      if (pointerId !== null && this.track?.hasPointerCapture?.(pointerId)) this.track.releasePointerCapture(pointerId);
    } catch {}
    this.resume('drag');
  }
  scheduleTouchScrollResume() {
    clearTimeout(this.touchScrollTimer);
    this.touchScrollTimer = setTimeout(() => {
      this.touchScrollTimer = null;
      this.motionPosition = this.track?.scrollLeft ?? null;
      this.resume('touch-scroll');
    }, 140);
  }
  setPersistentPause(paused) {
    const wasPaused = this.explicitlyPaused;
    this.explicitlyPaused = paused;
    this.syncMotion();
    if (wasPaused && !paused) this.dispatchEvent(new CustomEvent('dd:carousel-resume', { bubbles: true }));
  }
  pause(reason) { this.pauseReasons.add(reason); this.syncMotion(); }
  resume(reason) { this.pauseReasons.delete(reason); this.syncMotion(); }

  syncPurchasePause() {
    const open = this.querySelector('dialog[open], quick-add-modal[open], [data-dd-purchase][aria-hidden="false"]');
    if (open) this.pause('purchase');
    else this.resume('purchase');
  }

  updateControls() {
    const overflowing = this.isCarousel && this.items.length > 1 && (this.maxScroll > 1 || this.infinite);
    const controls = this.querySelector('[data-dd-carousel-controls]');
    if (controls) controls.hidden = !overflowing;
    if (this.previousButton) this.previousButton.disabled = !overflowing || (!this.infinite && this.track.scrollLeft <= 1);
    if (this.nextButton) this.nextButton.disabled = !overflowing || (!this.infinite && this.track.scrollLeft >= this.maxScroll - 1);
    if (this.progress && this.indicator) {
      const loopProgress = this.loopSpan > 0 ? (this.track.scrollLeft - this.loopOrigin) / this.loopSpan : 0;
      const ratio = this.infinite && this.loopSpan > 0
        ? Math.max(0, Math.min(1, loopProgress))
        : (this.maxScroll > 0 ? Math.max(0, Math.min(1, this.track.scrollLeft / this.maxScroll)) : 0);
      this.progress.setAttribute('role', 'progressbar');
      this.progress.setAttribute('aria-valuemin', '0');
      this.progress.setAttribute('aria-valuemax', '100');
      this.progress.setAttribute('aria-valuenow', String(Math.round(ratio * 100)));
      this.progress.setAttribute('aria-label', this.progress.dataset.label || 'Carousel');
      this.indicator.style.setProperty('--dd-carousel-indicator-width', '48px');
      this.indicator.style.setProperty('--dd-carousel-indicator-x', `${ratio * 112}px`);
    }
  }
}

if (!customElements.get('dd-continuous-carousel')) customElements.define('dd-continuous-carousel', DDContinuousCarousel);
})();
